import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Zap, Plus, Trash2, ToggleLeft, ToggleRight,
  ArrowRight, CheckCircle2, DollarSign, Bell, Target,
  FileText, Loader2, AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "@/lib/authFetch";
import { useAuth } from "@/hooks/useAuth";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Security audit (FIN-2 T1, 2026-04-30) — sections legacy.
 *
 * Backend já blindado pela SEC-1 (JWT obrigatório, query user_id ignorado).
 * Sem JWT, fetch retorna 401 e UI quebra.
 *
 * | Endpoint chamado                       | authFetch usado? | Notas       |
 * |----------------------------------------|------------------|-------------|
 * | GET  /api/automations?user_id=         | ❌ → ✅ (T2)     | listar      |
 * | POST /api/automations?user_id=         | ❌ → ✅ (T2)     | criar       |
 * | DEL  /api/automations/:id?user_id=     | ❌ → ✅ (T2)     | deletar     |
 * | PATCH /api/automations/:id/toggle      | ❌ → ✅ (T2)     | toggle      |
 *
 * Nota: query user_id é IGNORADO pelo backend (SEC-1, scoped_filter usa JWT).
 * Mantido por enquanto pra não quebrar a forma do call site.
 */

const TRIGGER_OPTIONS = [
  { value: "task_completed", label: "Tarefa concluída", icon: CheckCircle2, color: "brand-cyan" },
  { value: "habit_checked", label: "Hábito marcado", icon: Target, color: "brand-green" },
  { value: "expense_registered", label: "Gasto registrado", icon: DollarSign, color: "brand-purple" },
  { value: "reminder_completed", label: "Lembrete concluído", icon: Bell, color: "amber-400" },
];

const ACTION_OPTIONS = [
  { value: "checkin_habit", label: "Marcar hábito", icon: Target, paramKey: "name", paramLabel: "Nome do hábito" },
  { value: "create_task", label: "Criar tarefa", icon: CheckCircle2, paramKey: "title", paramLabel: "Título da tarefa" },
  { value: "create_reminder", label: "Criar lembrete", icon: Bell, paramKey: "message", paramLabel: "Mensagem do lembrete" },
  { value: "register_expense", label: "Registrar gasto", icon: DollarSign, paramKey: "description", paramLabel: "Descrição do gasto" },
];

const FILTER_OPTIONS = {
  task_completed: [
    { value: "title_contains", label: "Título contém" },
  ],
  habit_checked: [
    { value: "name_contains", label: "Nome contém" },
  ],
  expense_registered: [
    { value: "amount_above", label: "Valor acima de" },
    { value: "amount_below", label: "Valor abaixo de" },
    { value: "category_equals", label: "Categoria igual a" },
  ],
  reminder_completed: [
    { value: "title_contains", label: "Título contém" },
  ],
};

function AutomationCard({ automation, onDelete, onToggle }) {
  const trigger = TRIGGER_OPTIONS.find(t => t.value === automation.trigger_type);
  const TriggerIcon = trigger?.icon || Zap;
  const filterEntries = Object.entries(automation.trigger_filter || {});
  const actionDefs = automation.actions || [];

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`group relative p-5 rounded-2xl ${
        automation.is_active
          ? "glass-card glow-hover"
          : "glass-card opacity-60"
      }`}
      data-testid={`automation-card-${automation.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            automation.is_active ? `bg-${trigger?.color || "brand-cyan"}/10 border border-${trigger?.color || "brand-cyan"}/30` : "bg-slate-800 border border-slate-700"
          }`}>
            <TriggerIcon className={`w-5 h-5 ${automation.is_active ? `text-${trigger?.color || "brand-cyan"}` : "text-slate-500"}`} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white leading-tight">{automation.name}</h3>
            {automation.executions > 0 && (
              <span className="text-xs text-slate-500">{automation.executions}x executada</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onToggle(automation.id)}
            className="p-1.5 rounded-lg hover:bg-white/5 transition-colors"
            data-testid={`automation-toggle-${automation.id}`}
            title={automation.is_active ? "Desativar" : "Ativar"}
          >
            {automation.is_active
              ? <ToggleRight className="w-6 h-6 text-brand-green" />
              : <ToggleLeft className="w-6 h-6 text-slate-500" />
            }
          </button>
          <button
            onClick={() => onDelete(automation.id)}
            className="p-1.5 rounded-lg hover:bg-red-500/10 text-slate-500 hover:text-red-400 transition-colors"
            data-testid={`automation-delete-${automation.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Flow visualization */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="px-3 py-1.5 rounded-lg bg-brand-cyan/10 border border-brand-cyan/20 text-xs text-brand-cyan font-medium">
          {trigger?.label || automation.trigger_type}
          {filterEntries.length > 0 && (
            <span className="ml-1 text-brand-cyan/70">
              ({filterEntries.map(([k, v]) => `${v}`).join(", ")})
            </span>
          )}
        </div>

        <ArrowRight className="w-4 h-4 text-slate-500 flex-shrink-0" />

        {actionDefs.map((act, i) => {
          const actOpt = ACTION_OPTIONS.find(a => a.value === act.type);
          const ActIcon = actOpt?.icon || Zap;
          const paramValues = Object.values(act.params || {}).join(", ");
          return (
            <div key={i} className="px-3 py-1.5 rounded-lg bg-brand-purple/10 border border-brand-purple/20 text-xs text-brand-purple font-medium flex items-center gap-1.5">
              <ActIcon className="w-3.5 h-3.5" />
              {actOpt?.label || act.type}
              {paramValues && <span className="text-brand-purple/70">({paramValues})</span>}
            </div>
          );
        })}
      </div>

      {automation.last_executed && (
        <p className="mt-3 text-xs text-slate-600">
          Última execução: {new Date(automation.last_executed).toLocaleString("pt-BR")}
        </p>
      )}
    </motion.div>
  );
}

function CreateAutomationForm({ onCreated, userId }) {
  const [triggerType, setTriggerType] = useState("task_completed");
  const [filterKey, setFilterKey] = useState("title_contains");
  const [filterValue, setFilterValue] = useState("");
  const [actionType, setActionType] = useState("checkin_habit");
  const [actionValue, setActionValue] = useState("");
  const [saving, setSaving] = useState(false);

  const triggerFilters = FILTER_OPTIONS[triggerType] || [];
  const actionOpt = ACTION_OPTIONS.find(a => a.value === actionType);

  useEffect(() => {
    const filters = FILTER_OPTIONS[triggerType] || [];
    if (filters.length > 0) setFilterKey(filters[0].value);
  }, [triggerType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!filterValue.trim() || !actionValue.trim()) {
      toast.error("Preencha todos os campos");
      return;
    }

    setSaving(true);
    try {
      const trigger = TRIGGER_OPTIONS.find(t => t.value === triggerType);
      const name = `Quando ${trigger?.label?.toLowerCase() || triggerType} '${filterValue}' → ${actionOpt?.label?.toLowerCase() || actionType} '${actionValue}'`;

      const params = new URLSearchParams({
        user_id: userId || "default_user",
        name,
        trigger_type: triggerType,
        trigger_filter_key: filterKey,
        trigger_filter_value: filterValue,
        action_type: actionType,
        action_param_key: actionOpt?.paramKey || "name",
        action_param_value: actionValue,
      });

      const res = await authFetch(`${API_URL}/api/automations?${params}`, { method: "POST" });
      if (!res.ok) throw new Error("Failed to create");
      toast.success("Automação criada!");
      setFilterValue("");
      setActionValue("");
      onCreated?.();
    } catch (err) {
      toast.error("Erro ao criar automação");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-5 rounded-2xl border border-dashed border-brand-cyan/30 bg-brand-cyan/5 space-y-4" data-testid="create-automation-form">
      <h3 className="text-base font-semibold text-white flex items-center gap-2">
        <Plus className="w-4 h-4 text-brand-cyan" />
        Nova Automação
      </h3>

      {/* QUANDO */}
      <div className="space-y-2">
        <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Quando</label>
        <div className="flex gap-2 flex-wrap">
          {TRIGGER_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setTriggerType(opt.value)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border transition-all ${
                triggerType === opt.value
                  ? "bg-brand-cyan/10 border-brand-cyan/40 text-brand-cyan"
                  : "bg-nectar-card border-nectar-border text-slate-400 hover:text-white hover:border-slate-600"
              }`}
              data-testid={`trigger-${opt.value}`}
            >
              <opt.icon className="w-3.5 h-3.5" />
              {opt.label}
            </button>
          ))}
        </div>

        <div className="flex gap-2">
          {triggerFilters.length > 1 && (
            <select
              value={filterKey}
              onChange={e => setFilterKey(e.target.value)}
              className="bg-nectar-card border border-nectar-border rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-cyan/50"
            >
              {triggerFilters.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          )}
          <input
            type="text"
            value={filterValue}
            onChange={e => setFilterValue(e.target.value)}
            placeholder={filterKey.includes("amount") ? "Valor (ex: 100)" : "Texto (ex: treino)"}
            className="flex-1 bg-nectar-card border border-nectar-border rounded-xl px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-brand-cyan/50"
            data-testid="automation-filter-input"
          />
        </div>
      </div>

      {/* ENTÃO */}
      <div className="flex items-center gap-2">
        <div className="h-px flex-1 bg-nectar-border" />
        <ArrowRight className="w-4 h-4 text-slate-500" />
        <span className="text-xs text-slate-500 font-medium">ENTÃO</span>
        <div className="h-px flex-1 bg-nectar-border" />
      </div>

      <div className="space-y-2">
        <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Executar</label>
        <div className="flex gap-2 flex-wrap">
          {ACTION_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setActionType(opt.value)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border transition-all ${
                actionType === opt.value
                  ? "bg-brand-purple/10 border-brand-purple/40 text-brand-purple"
                  : "bg-nectar-card border-nectar-border text-slate-400 hover:text-white hover:border-slate-600"
              }`}
              data-testid={`action-${opt.value}`}
            >
              <opt.icon className="w-3.5 h-3.5" />
              {opt.label}
            </button>
          ))}
        </div>

        <input
          type="text"
          value={actionValue}
          onChange={e => setActionValue(e.target.value)}
          placeholder={actionOpt?.paramLabel || "Parâmetro"}
          className="w-full bg-nectar-card border border-nectar-border rounded-xl px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-brand-cyan/50"
          data-testid="automation-action-input"
        />
      </div>

      <button
        type="submit"
        disabled={saving || !filterValue.trim() || !actionValue.trim()}
        className="w-full py-3 rounded-xl bg-brand-cyan/10 border border-brand-cyan/30 text-brand-cyan font-medium text-sm hover:bg-brand-cyan/20 hover:shadow-glow-cyan transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        data-testid="create-automation-submit"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
        {saving ? "Criando..." : "Criar Automação"}
      </button>
    </form>
  );
}

export default function AutomationsSection() {
  const { user } = useAuth();
  const userId = user?.id || "default_user";
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAutomations = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/automations?user_id=${userId}`);
      if (res.ok) {
        const data = await res.json();
        setAutomations(data.automations || []);
      }
    } catch (err) {
      console.error("Error fetching automations:", err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchAutomations(); }, [fetchAutomations]);

  const handleDelete = async (id) => {
    try {
      const res = await authFetch(`${API_URL}/api/automations/${id}?user_id=${userId}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Automação removida");
        fetchAutomations();
      }
    } catch { toast.error("Erro ao remover"); }
  };

  const handleToggle = async (id) => {
    try {
      const res = await authFetch(`${API_URL}/api/automations/${id}/toggle?user_id=${userId}`, { method: "PATCH" });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.is_active ? "Automação ativada" : "Automação desativada");
        fetchAutomations();
      }
    } catch { toast.error("Erro ao alternar"); }
  };

  const active = automations.filter(a => a.is_active);
  const inactive = automations.filter(a => !a.is_active);

  return (
    <div className="p-6 md:p-8 space-y-8 max-w-3xl" data-testid="automations-section">
      {/* Header */}
      <div>
        <h2 className="font-display text-2xl font-bold text-white mb-1 flex items-center gap-3">
          <Zap className="w-6 h-6 text-brand-cyan" />
          Automações
        </h2>
        <p className="text-sm text-slate-400">
          Crie regras que executam ações automaticamente. Tipo IFTTT para sua vida.
        </p>
      </div>

      {/* Create form */}
      <CreateAutomationForm onCreated={fetchAutomations} userId={userId} />

      {/* Active automations */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando...
        </div>
      ) : active.length === 0 && inactive.length === 0 ? (
        <div className="text-center py-12">
          <AlertCircle className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">Nenhuma automação ainda.</p>
          <p className="text-slate-500 text-xs mt-1">Crie sua primeira regra acima ou diga ao Nexo: "quando completar treino, marca exercício"</p>
        </div>
      ) : (
        <div className="space-y-6">
          {active.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-slate-400 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-brand-green animate-pulse" />
                Ativas ({active.length})
              </h3>
              <AnimatePresence>
                {active.map(a => (
                  <AutomationCard key={a.id} automation={a} onDelete={handleDelete} onToggle={handleToggle} />
                ))}
              </AnimatePresence>
            </div>
          )}
          {inactive.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-slate-500">Inativas ({inactive.length})</h3>
              <AnimatePresence>
                {inactive.map(a => (
                  <AutomationCard key={a.id} automation={a} onDelete={handleDelete} onToggle={handleToggle} />
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
